import { Trans } from 'react-i18next';
import { useTranslationWithPrefix } from '@hotjar/translations';
import styled from '@emotion/styled';
import { Accordion, Alert, Button, Modal, Stack, Text, tokens } from '@hotjar/hotsauce';
import InstructionVideoPlaceholderImage from '@hotjar/shared-assets/images/payments/delete-payment-method-instructions.gif';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useOwnedAccount } from '@hotjar/account';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';

const TRANS_KEY = 'billing:payment-details.delete';
const MODAL_NAME = 'Delete payment active plan warning';

const S = {
  Img: styled.img`
    width: 100%;
    margin-bottom: ${tokens.spacer.x3};
  `,
  List: styled.ol`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x0, direction: 'column' })}
    padding-left: ${tokens.spacer.x2};
    padding-right: ${tokens.spacer.x2};
  `,
};

export function DeletePaidPlanPaymentMethod({ onCancel }: GlobalModalProps<void>): JSX.Element {
  const { t: tBilling } = useTranslationWithPrefix(TRANS_KEY);

  const { loadedModal, closedModal, getGroupDataForAccount, click } = useTrack();
  const { data: account } = useOwnedAccount();

  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
    },
    [loadedModal, account, getGroupDataForAccount],
    () => account !== undefined,
  );

  const handleModalClose = () => {
    closedModal(MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
    onCancel();
  };

  return (
    <Modal
      isOpen
      contentLabel={tBilling('paid-plan.title')}
      onClose={() => {
        handleModalClose();
      }}
    >
      <Modal.Header title={tBilling('paid-plan.title')} />
      <Modal.Body>
        <Stack direction="column" spacing="x3">
          <div>
            {tBilling<string, string[]>('paid-plan.description', { returnObjects: true }).map(
              (text) => (
                <Text as="p" textType="body2" key={text}>
                  {text}
                </Text>
              ),
            )}
          </div>
          <Alert kind="info">{tBilling('paid-plan.discounts-and-benefits-warning')}</Alert>
          <Accordion
            onChange={() =>
              click('view tutorial', MODAL_NAME, account ? getGroupDataForAccount(account) : {})
            }
          >
            <Accordion.Section title={tBilling('paid-plan.instructions.title')}>
              <S.Img
                alt={tBilling('paid-plan.instructions.title')}
                src={InstructionVideoPlaceholderImage}
              />
              <S.List>
                {/*
                  NOTE: we did some questionable things in the translation file to make this work.
                  ideally we should only need the `instructions.steps` key's step to pass into
                  <Trans i18nKey={step} />, but then i18next can't find the translations and
                  creates additional entries
                 */}
                {tBilling<string, string[]>('paid-plan.instructions.steps', {
                  returnObjects: true,
                }).map((step) => (
                  <Text as="li" key={step}>
                    <Trans
                      i18nKey={`${TRANS_KEY}.paid-plan.instructions.${step}`}
                      components={[<strong />]}
                    />
                  </Text>
                ))}
              </S.List>
            </Accordion.Section>
          </Accordion>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => {
            click('submit', MODAL_NAME, account ? getGroupDataForAccount(account) : undefined);
            onCancel();
          }}
        >
          {tBilling('paid-plan.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const openDeletePaidPlanPaymentMethodModal = createGlobalModal<void, void>(
  DeletePaidPlanPaymentMethod,
);
